import { useEffect, useState, useCallback } from 'react';
import { api, errMsg, API_BASE } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, MessageSquareText, Cpu, Database, PlayCircle, Eye, Loader2, Sparkles, LayoutDashboard, RefreshCw, Download, TriangleAlert, TrendingUp, Info, Globe } from 'lucide-react';

const CHART_COLORS = [
  'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))',
  'hsl(var(--chart-4))', 'hsl(var(--chart-5))',
];

function EmptyRow({ colSpan, label }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="text-center text-sm text-muted-foreground py-10">{label}</TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------- Entities
function EntitiesTab() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', domain: '', aliases: '', is_own_brand: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/citetrack/entities'); setRows(r.data); }
    catch (e) { toast.error(errMsg(e, 'Failed to load entities')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: '', domain: '', aliases: '', is_own_brand: false }); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name, domain: row.domain || '', aliases: (row.aliases || []).join(', '), is_own_brand: !!row.is_own_brand });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const body = {
      name: form.name.trim(),
      domain: form.domain.trim(),
      aliases: form.aliases.split(',').map((a) => a.trim()).filter(Boolean),
      is_own_brand: form.is_own_brand,
    };
    try {
      if (editing) await api.put(`/citetrack/entities/${editing.id}`, body);
      else await api.post('/citetrack/entities', body);
      toast.success(editing ? 'Entity updated' : 'Entity created');
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete entity "${row.name}"?`)) return;
    try { await api.delete(`/citetrack/entities/${row.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-4" data-testid="citetrack-entities">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Brands and competitors you want to track across answer engines.</p>
        <Button onClick={openCreate} data-testid="citetrack-entity-add"><Plus className="h-4 w-4 mr-1.5" /> Add entity</Button>
      </div>
      <Card className="border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Domain</TableHead>
              <TableHead>Aliases</TableHead><TableHead>Own brand</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && <TableRow><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>}
            {rows && rows.length === 0 && <EmptyRow colSpan={5} label="No entities yet. Add your brand and competitors." />}
            {rows && rows.map((r) => (
              <TableRow key={r.id} data-testid={`citetrack-entity-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-muted-foreground">{r.domain || '—'}</TableCell>
                <TableCell className="text-muted-foreground">{(r.aliases || []).join(', ') || '—'}</TableCell>
                <TableCell>{r.is_own_brand ? <Badge>Own brand</Badge> : <Badge variant="secondary">Competitor</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`citetrack-entity-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`citetrack-entity-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="citetrack-entity-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit entity' : 'New entity'}</DialogTitle>
            <DialogDescription>Track a brand or competitor and its known aliases.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Notion" data-testid="citetrack-entity-name" /></div>
            <div><Label>Domain</Label><Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="notion.so" data-testid="citetrack-entity-domain" /></div>
            <div><Label>Aliases (comma separated)</Label><Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="Notion Labs, Notion HQ" data-testid="citetrack-entity-aliases" /></div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="text-sm font-medium">This is my own brand</div><div className="text-xs text-muted-foreground">Used to separate your brand from competitors.</div></div>
              <Switch checked={form.is_own_brand} onCheckedChange={(v) => setForm({ ...form, is_own_brand: v })} data-testid="citetrack-entity-ownbrand" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="citetrack-entity-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- Prompts
function PromptsTab() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ text: '', category: 'general', active: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/citetrack/prompts'); setRows(r.data); }
    catch (e) { toast.error(errMsg(e, 'Failed to load prompts')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ text: '', category: 'general', active: true }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ text: row.text, category: row.category || 'general', active: !!row.active }); setOpen(true); };

  const save = async () => {
    if (!form.text.trim()) { toast.error('Prompt text is required'); return; }
    setSaving(true);
    const body = { text: form.text.trim(), category: form.category.trim() || 'general', active: form.active };
    try {
      if (editing) await api.put(`/citetrack/prompts/${editing.id}`, body);
      else await api.post('/citetrack/prompts', body);
      toast.success(editing ? 'Prompt updated' : 'Prompt created');
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm('Delete this prompt?')) return;
    try { await api.delete(`/citetrack/prompts/${row.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-4" data-testid="citetrack-prompts">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Questions we'll ask each engine on every run.</p>
        <Button onClick={openCreate} data-testid="citetrack-prompt-add"><Plus className="h-4 w-4 mr-1.5" /> Add prompt</Button>
      </div>
      <Card className="border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[55%]">Prompt</TableHead><TableHead>Category</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && <TableRow><TableCell colSpan={4}><Skeleton className="h-8 w-full" /></TableCell></TableRow>}
            {rows && rows.length === 0 && <EmptyRow colSpan={4} label="No prompts yet. Add the questions you want tracked." />}
            {rows && rows.map((r) => (
              <TableRow key={r.id} data-testid={`citetrack-prompt-row-${r.id}`}>
                <TableCell className="font-medium">{r.text}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{r.category}</Badge></TableCell>
                <TableCell>{r.active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`citetrack-prompt-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`citetrack-prompt-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="citetrack-prompt-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit prompt' : 'New prompt'}</DialogTitle>
            <DialogDescription>This exact text is sent to every active engine.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Prompt text *</Label><Textarea rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="What is the best note-taking app?" data-testid="citetrack-prompt-text" /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="discovery" data-testid="citetrack-prompt-category" /></div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="text-sm font-medium">Active</div><div className="text-xs text-muted-foreground">Inactive prompts are skipped by the scheduler.</div></div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="citetrack-prompt-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="citetrack-prompt-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- Engines
function EnginesTab() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', access_method: 'api', api_config: '{}', active: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/citetrack/engines'); setRows(r.data); }
    catch (e) { toast.error(errMsg(e, 'Failed to load engines')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: '', access_method: 'api', api_config: '{\n  "model": ""\n}', active: true }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ name: row.name, access_method: row.access_method, api_config: JSON.stringify(row.api_config || {}, null, 2), active: !!row.active }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    let cfg = {};
    try { cfg = form.api_config.trim() ? JSON.parse(form.api_config) : {}; }
    catch (_e) { toast.error('API config must be valid JSON'); return; }
    setSaving(true);
    const body = { name: form.name.trim(), access_method: form.access_method, api_config: cfg, active: form.active };
    try {
      if (editing) await api.put(`/citetrack/engines/${editing.id}`, body);
      else await api.post('/citetrack/engines', body);
      toast.success(editing ? 'Engine updated' : 'Engine created');
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete engine "${row.name}"?`)) return;
    try { await api.delete(`/citetrack/engines/${row.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-4" data-testid="citetrack-engines">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Answer engines we query. API engines call directly; browser engines run via the (Phase 5) Playwright worker.</p>
        <Button onClick={openCreate} data-testid="citetrack-engine-add"><Plus className="h-4 w-4 mr-1.5" /> Add engine</Button>
      </div>
      <Card className="border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Access method</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && <TableRow><TableCell colSpan={4}><Skeleton className="h-8 w-full" /></TableCell></TableRow>}
            {rows && rows.length === 0 && <EmptyRow colSpan={4} label="No engines yet." />}
            {rows && rows.map((r) => (
              <TableRow key={r.id} data-testid={`citetrack-engine-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>
                  {r.access_method === 'api'
                    ? <Badge className="bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30">API</Badge>
                    : <Badge variant="outline">Browser</Badge>}
                </TableCell>
                <TableCell>{r.active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`citetrack-engine-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`citetrack-engine-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="citetrack-engine-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit engine' : 'New engine'}</DialogTitle>
            <DialogDescription>Configure how we reach this answer engine.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="OpenAI" data-testid="citetrack-engine-name" /></div>
            <div>
              <Label>Access method</Label>
              <Select value={form.access_method} onValueChange={(v) => setForm({ ...form, access_method: v })}>
                <SelectTrigger data-testid="citetrack-engine-access"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="api">API (direct call)</SelectItem>
                  <SelectItem value="browser">Browser (Playwright worker)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>API config (JSON)</Label><Textarea rows={4} className="font-mono text-xs" value={form.api_config} onChange={(e) => setForm({ ...form, api_config: e.target.value })} data-testid="citetrack-engine-config" /></div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="text-sm font-medium">Active</div><div className="text-xs text-muted-foreground">Inactive engines are skipped by the scheduler.</div></div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="citetrack-engine-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="citetrack-engine-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function statusBadgeRun(status) {
  if (status === 'success') return <Badge>Success</Badge>;
  if (status === 'failed') return <Badge variant="destructive">Failed</Badge>;
  return <Badge variant="secondary" className="capitalize">{status}</Badge>;
}

// ---------------------------------------------------------------- Runs
function mentionBadge(type) {
  const map = {
    cited: <Badge className="bg-emerald-600 hover:bg-emerald-600">Cited</Badge>,
    named: <Badge>Named</Badge>,
    paraphrased: <Badge variant="secondary">Paraphrased</Badge>,
    absent: <Badge variant="outline" className="text-muted-foreground">Absent</Badge>,
  };
  return map[type] || <Badge variant="secondary">{type}</Badge>;
}

function sentimentDot(s) {
  const c = s === 'positive' ? 'bg-emerald-500' : s === 'negative' ? 'bg-rose-500' : 'bg-slate-400';
  return <span className="inline-flex items-center gap-1 text-xs capitalize"><span className={`h-2 w-2 rounded-full ${c}`} />{s}</span>;
}

function RunsTab() {
  const [rows, setRows] = useState(null);
  const [running, setRunning] = useState(false);
  const [extractingAll, setExtractingAll] = useState(false);
  const [view, setView] = useState(null);
  const [mentions, setMentions] = useState(null);
  const [extractingOne, setExtractingOne] = useState(false);
  const [browserRunning, setBrowserRunning] = useState(false);
  const [browserStatus, setBrowserStatus] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/citetrack/runs'); setRows(r.data); }
    catch (e) { toast.error(errMsg(e, 'Failed to load runs')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/citetrack/runs/sweep');
      const s = r.data;
      toast.success(`Sweep complete — ${s.runs_created} run(s): ${s.success} ok, ${s.failed} failed${s.browser_engines_skipped ? `, ${s.browser_engines_skipped} browser skipped` : ''}`);
      load();
    } catch (e) { toast.error(errMsg(e, 'Sweep failed')); }
    finally { setRunning(false); }
  };

  const runBrowser = async () => {
    setBrowserRunning(true);
    setBrowserStatus(null);
    try {
      const r = await api.post('/citetrack/runs/browser-sweep');
      toast.message('Browser sweep started', { description: `${r.data.expected_runs} run(s) via Playwright — this can take a while.` });
      // Poll status until it leaves the running state.
      let done = false;
      for (let i = 0; i < 60 && !done; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 4000));
        // eslint-disable-next-line no-await-in-loop
        const st = await api.get('/citetrack/runs/browser-status');
        setBrowserStatus(st.data);
        if (st.data.state !== 'running') done = true;
      }
      const s = (await api.get('/citetrack/runs/browser-status')).data;
      if (s.state === 'error') toast.error(`Browser sweep error: ${(s.error || '').slice(0, 120)}`);
      else toast.success(`Browser sweep done — ${s.success ?? 0} ok, ${s.failed ?? 0} failed`);
      load();
    } catch (e) { toast.error(errMsg(e, 'Browser sweep failed')); }
    finally { setBrowserRunning(false); }
  };

  const extractPending = async () => {
    setExtractingAll(true);
    try {
      const r = await api.post('/citetrack/runs/extract-pending');
      const s = r.data;
      toast.success(`Extracted ${s.done} run(s)${s.failed ? `, ${s.failed} failed` : ''} (of ${s.processed} pending)`);
      load();
    } catch (e) { toast.error(errMsg(e, 'Extraction failed')); }
    finally { setExtractingAll(false); }
  };

  const openView = async (run) => {
    setMentions(null);
    try {
      const r = await api.get(`/citetrack/runs/${run.id}`);
      setView(r.data);
      const m = await api.get(`/citetrack/runs/${run.id}/mentions`);
      setMentions(m.data);
    } catch (e) { toast.error(errMsg(e, 'Failed to load run')); }
  };

  const extractOne = async () => {
    if (!view) return;
    setExtractingOne(true);
    try {
      const r = await api.post(`/citetrack/runs/${view.id}/extract`);
      if (r.data.status === 'done') toast.success(`Extracted ${r.data.mentions} mention(s), ${r.data.present} present`);
      else toast.message('Extraction ' + r.data.status, { description: r.data.reason || r.data.error || '' });
      const m = await api.get(`/citetrack/runs/${view.id}/mentions`);
      setMentions(m.data);
      load();
    } catch (e) { toast.error(errMsg(e, 'Extraction failed')); }
    finally { setExtractingOne(false); }
  };

  return (
    <div className="space-y-4" data-testid="citetrack-runs">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">Each run queries one prompt against one engine. API engines call directly; browser engines run via the isolated Playwright worker.</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runBrowser} disabled={browserRunning} data-testid="citetrack-run-browser">
            {browserRunning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Globe className="h-4 w-4 mr-1.5" />}
            {browserRunning ? 'Running browser…' : 'Run browser engines'}
          </Button>
          <Button variant="outline" onClick={extractPending} disabled={extractingAll} data-testid="citetrack-extract-pending">
            {extractingAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            {extractingAll ? 'Extracting…' : 'Extract pending'}
          </Button>
          <Button onClick={runNow} disabled={running} data-testid="citetrack-run-now">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1.5" />}
            {running ? 'Running…' : 'Run now'}
          </Button>
        </div>
      </div>

      {browserRunning && browserStatus && (
        <div className="rounded-lg border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/8 p-3 text-sm flex items-center gap-2" data-testid="citetrack-browser-progress">
          <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--accent))]" />
          <span>Browser worker: {browserStatus.created ?? 0}/{(browserStatus.prompts ?? 0) * (browserStatus.browser_engines ?? 0)} run(s) — {browserStatus.success ?? 0} ok, {browserStatus.failed ?? 0} failed</span>
        </div>
      )}
      <Card className="border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[38%]">Prompt</TableHead><TableHead>Engine</TableHead>
              <TableHead>Status</TableHead><TableHead>Extraction</TableHead>
              <TableHead>When</TableHead><TableHead className="text-right">View</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && <TableRow><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>}
            {rows && rows.length === 0 && <EmptyRow colSpan={6} label="No runs yet. Add active prompts + engines, then click Run now." />}
            {rows && rows.map((r) => (
              <TableRow key={r.id} data-testid={`citetrack-run-row-${r.id}`}>
                <TableCell className="font-medium truncate max-w-[340px]">{r.prompt_text}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5">
                    {r.engine_name}
                    {r.provider_mode === 'browser' && <Badge variant="outline" className="text-[10px]"><Globe className="h-3 w-3 mr-1" />Browser</Badge>}
                  </span>
                </TableCell>
                <TableCell>{statusBadgeRun(r.status)}</TableCell>
                <TableCell>
                  {r.extraction_status === 'done'
                    ? <Badge variant="outline" className="text-emerald-600 border-emerald-600/40">{r.mentions_count ?? 0} mentions</Badge>
                    : r.extraction_status === 'failed'
                      ? <Badge variant="destructive">Failed</Badge>
                      : r.extraction_status === 'skipped'
                        ? <Badge variant="secondary">Skipped</Badge>
                        : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">{String(r.timestamp || '').slice(0, 16).replace('T', ' ')}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openView(r)} data-testid={`citetrack-run-view-${r.id}`}><Eye className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl" data-testid="citetrack-run-dialog">
          <DialogHeader>
            <DialogTitle>Run detail</DialogTitle>
            <DialogDescription>{view?.engine_name} · {view?.status} · {view?.provider_mode === 'browser' ? 'browser' : (view?.model || '—')}</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="space-y-4">
              <div><div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Prompt</div><div className="text-sm">{view.prompt_text}</div></div>
              {view.error && (
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-800">
                  {view.error_type && <span className="font-medium">[{view.error_type}] </span>}{view.error}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Mentions</div>
                  {view.status === 'success' && (
                    <Button size="sm" variant="outline" onClick={extractOne} disabled={extractingOne} data-testid="citetrack-run-extract">
                      {extractingOne ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                      {mentions && mentions.length ? 'Re-extract' : 'Extract mentions'}
                    </Button>
                  )}
                </div>
                {mentions === null && <Skeleton className="h-10 w-full" />}
                {mentions && mentions.length === 0 && <div className="text-sm text-muted-foreground py-2">No mentions extracted yet.</div>}
                {mentions && mentions.length > 0 && (
                  <div className="space-y-2" data-testid="citetrack-run-mentions">
                    {mentions.map((m) => (
                      <div key={m.id} className="rounded-lg border p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{m.entity_name}</span>
                            {m.is_own_brand && <Badge className="bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30 text-[10px]">Own brand</Badge>}
                          </div>
                          <div className="flex items-center gap-2">{mentionBadge(m.mention_type)}<Badge variant="outline" className="capitalize text-[10px]">{m.position}</Badge>{sentimentDot(m.sentiment)}</div>
                        </div>
                        {m.snippet && <div className="text-xs text-muted-foreground mt-1.5 italic">“{m.snippet}”</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Raw response</div>
                <div className="max-h-[30vh] overflow-auto rounded-lg border bg-secondary/40 p-3 text-sm whitespace-pre-wrap" data-testid="citetrack-run-raw">
                  {view.raw_response || '(empty)'}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setView(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- Dashboard (Phase 4)
function gridCellBadge(type) {
  if (!type) return <span className="text-[11px] text-muted-foreground">—</span>;
  const map = {
    cited: <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">Cited</Badge>,
    named: <Badge className="text-[10px]">Named</Badge>,
    paraphrased: <Badge variant="secondary" className="text-[10px]">Paraphrased</Badge>,
    absent: <Badge variant="outline" className="text-muted-foreground text-[10px]">Absent</Badge>,
  };
  return map[type] || <Badge variant="secondary" className="text-[10px]">{type}</Badge>;
}

function AlertCard({ alert }) {
  const cfg = {
    warning: { icon: TriangleAlert, cls: 'border-amber-500/40 bg-amber-500/10', ic: 'text-amber-600' },
    success: { icon: TrendingUp, cls: 'border-emerald-500/40 bg-emerald-500/10', ic: 'text-emerald-600' },
    info: { icon: Info, cls: 'border-sky-500/40 bg-sky-500/10', ic: 'text-sky-600' },
  }[alert.severity] || { icon: Info, cls: 'border-border bg-secondary/40', ic: 'text-muted-foreground' };
  const Icon = cfg.icon;
  return (
    <div className={`flex items-start gap-3 rounded-lg border p-3 ${cfg.cls}`} data-testid="citetrack-alert">
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cfg.ic}`} />
      <div>
        <div className="text-sm font-medium">{alert.title}</div>
        {alert.detail && <div className="text-xs text-muted-foreground mt-0.5">{alert.detail}</div>}
      </div>
    </div>
  );
}

function DashboardTab() {
  const [data, setData] = useState(null);
  const [entityId, setEntityId] = useState('');
  const [engineId, setEngineId] = useState('all');
  const [rebuilding, setRebuilding] = useState(false);

  const load = useCallback(async (opts = {}) => {
    try {
      const params = {};
      const eid = opts.entityId !== undefined ? opts.entityId : entityId;
      const gid = opts.engineId !== undefined ? opts.engineId : engineId;
      if (eid) params.entity_id = eid;
      if (gid && gid !== 'all') params.engine_id = gid;
      const r = await api.get('/citetrack/dashboard', { params });
      setData(r.data);
      if (!entityId && r.data.focus_entity_id) setEntityId(r.data.focus_entity_id);
    } catch (e) { toast.error(errMsg(e, 'Failed to load dashboard')); setData({ has_data: false, entities: [], engines: [], share_series: [], grid: { prompts: [], engines: [], cells: {} }, alerts: [] }); }
  }, [entityId, engineId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await api.post('/citetrack/aggregate');
      toast.success(`Aggregates rebuilt — ${r.data.aggregate_rows} row(s) across ${r.data.weeks} week(s)`);
      await load();
    } catch (e) { toast.error(errMsg(e, 'Rebuild failed')); }
    finally { setRebuilding(false); }
  };

  const downloadCsv = () => {
    const token = localStorage.getItem('ageorush_token');
    fetch(`${API_BASE}/citetrack/dashboard.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        const blob = await r.blob();
        const dl = document.createElement('a');
        dl.href = URL.createObjectURL(blob);
        dl.download = `citetrack-aggregates-${new Date().toISOString().slice(0, 10)}.csv`;
        dl.click();
        URL.revokeObjectURL(dl.href);
      })
      .catch((e) => toast.error(e.message || 'Export failed'));
  };

  const onEntity = (v) => { setEntityId(v); load({ entityId: v }); };
  const onEngine = (v) => { setEngineId(v); load({ engineId: v }); };

  if (data === null) {
    return <div className="space-y-4" data-testid="citetrack-dashboard"><Skeleton className="h-10 w-full" /><Skeleton className="h-64 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }

  const entities = data.entities || [];
  const engines = data.engines || [];
  const series = data.share_series || [];
  const grid = data.grid || { prompts: [], engines: [], cells: {} };
  const alerts = data.alerts || [];
  const focus = entities.find((e) => e.id === (entityId || data.focus_entity_id));

  return (
    <div className="space-y-5" data-testid="citetrack-dashboard">
      {/* Controls */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Focus entity</Label>
            <Select value={entityId || data.focus_entity_id || ''} onValueChange={onEntity}>
              <SelectTrigger className="w-[180px]" data-testid="citetrack-dash-entity"><SelectValue placeholder="Entity" /></SelectTrigger>
              <SelectContent>
                {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.is_own_brand ? ' (own)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Engine</Label>
            <Select value={engineId} onValueChange={onEngine}>
              <SelectTrigger className="w-[170px]" data-testid="citetrack-dash-engine"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All engines</SelectItem>
                {engines.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadCsv} disabled={!data.has_data} data-testid="citetrack-dash-csv"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          <Button onClick={rebuild} disabled={rebuilding} data-testid="citetrack-dash-rebuild">
            {rebuilding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            {rebuilding ? 'Rebuilding…' : 'Rebuild aggregates'}
          </Button>
        </div>
      </div>

      {!data.has_data ? (
        <Card className="border">
          <EmptyState
            title="No aggregated data yet"
            description="Run some prompts, extract mentions, then click “Rebuild aggregates” to populate weekly citation-share trends."
            action={<Button onClick={rebuild} disabled={rebuilding} data-testid="citetrack-dash-rebuild-empty"><RefreshCw className="h-4 w-4 mr-1.5" /> Rebuild aggregates</Button>}
          />
        </Card>
      ) : (
        <>
          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="space-y-2" data-testid="citetrack-dash-alerts">
              {alerts.map((a, i) => <AlertCard key={i} alert={a} />)}
            </div>
          )}

          {/* Citation-share line chart */}
          <ChartCard
            title="Citation share over time"
            subtitle={`Weekly % of answers that mention each entity${engineId !== 'all' ? ' · single engine' : ' · all engines'}`}
            testId="citetrack-dash-share-card"
          >
            <div className="h-72" data-testid="citetrack-dash-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="week" fontSize={11} tickFormatter={(w) => (w || '').slice(5)} />
                  <YAxis fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <RTooltip
                    formatter={(val, key) => [`${val}%`, (entities.find((e) => e.id === key) || {}).name || key]}
                    labelFormatter={(w) => `Week of ${w}`}
                  />
                  <Legend formatter={(key) => (entities.find((e) => e.id === key) || {}).name || key} />
                  {entities.map((e, i) => (
                    <Line
                      key={e.id}
                      type="monotone"
                      dataKey={e.id}
                      name={e.id}
                      stroke={CHART_COLORS[i % CHART_COLORS.length]}
                      strokeWidth={e.is_own_brand ? 3 : 2}
                      dot={{ r: 2 }}
                      strokeDasharray={e.is_own_brand ? '' : '5 3'}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          {/* Prompts × Engines grid */}
          <Card className="border p-5" data-testid="citetrack-dash-grid-card">
            <div className="font-display text-base font-semibold mb-1">Prompts × Engines</div>
            <div className="text-xs text-muted-foreground mb-3">Latest mention of <span className="font-medium text-foreground">{focus?.name || 'the focus entity'}</span> for each active prompt on each engine.</div>
            {grid.prompts.length === 0 || grid.engines.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No active prompts or engines to display.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="citetrack-dash-grid">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-3 font-medium text-[11px] uppercase tracking-wide text-muted-foreground w-[45%]">Prompt</th>
                      {grid.engines.map((e) => (
                        <th key={e.id} className="text-center py-2 px-2 font-medium text-[11px] uppercase tracking-wide text-muted-foreground">{e.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.prompts.map((p) => (
                      <tr key={p.id} className="border-b last:border-0" data-testid={`citetrack-grid-row-${p.id}`}>
                        <td className="py-2 pr-3 truncate max-w-[320px]">{p.text}</td>
                        {grid.engines.map((e) => {
                          const cell = grid.cells[`${p.id}::${e.id}`];
                          return <td key={e.id} className="py-2 px-2 text-center">{gridCellBadge(cell?.mention_type)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export default function CiteTrackPage() {
  return (
    <div className="space-y-6" data-testid="citetrack-page">
      <div className="flex items-center gap-2">
        <Database className="h-5 w-5 text-[hsl(var(--accent))]" />
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">CiteTrack</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Admin control room for AEO citation tracking — dashboards, entities, prompts, engines, API & browser runs. <span className="text-[hsl(var(--accent))]">Phases 1–5</span> live.</p>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList data-testid="citetrack-tabs">
          <TabsTrigger value="dashboard" data-testid="citetrack-tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="entities" data-testid="citetrack-tab-entities"><Building2 className="h-4 w-4 mr-1.5" /> Entities</TabsTrigger>
          <TabsTrigger value="prompts" data-testid="citetrack-tab-prompts"><MessageSquareText className="h-4 w-4 mr-1.5" /> Prompts</TabsTrigger>
          <TabsTrigger value="engines" data-testid="citetrack-tab-engines"><Cpu className="h-4 w-4 mr-1.5" /> Engines</TabsTrigger>
          <TabsTrigger value="runs" data-testid="citetrack-tab-runs"><PlayCircle className="h-4 w-4 mr-1.5" /> Runs</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-5"><DashboardTab /></TabsContent>
        <TabsContent value="entities" className="mt-5"><EntitiesTab /></TabsContent>
        <TabsContent value="prompts" className="mt-5"><PromptsTab /></TabsContent>
        <TabsContent value="engines" className="mt-5"><EnginesTab /></TabsContent>
        <TabsContent value="runs" className="mt-5"><RunsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
